/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Program: create, publish, go live, cancel.  CA-24, CA-33.
 *
 * - Times are wall-clock strings at the mosque ('2026-09-21T18:00:00'),
 *   sent and shown as such — never an instant (a 19:00 milad became 21:00).
 * - A draft nobody has seen may be deleted (two clicks); anything published
 *   is only ever cancelled, because people may have planned around it.
 * - The list is `items` (fault 75: `upcoming` + a COUNT printed "undefined").
 * - The form works on an empty programme too.
 * - «Publiser» and «Avlys» are two clicks whose second says what happens (QA A13, 2026-10-16), and disabled while they
 *   run; the folded sections (Påmelding, Koblede arrangementer, Varsel) say when they could not be fetched and offer
 *   «Prøv igjen», instead of «Henter …» for ever.
 * - Arrangementer pro (owner 2026-10-31): no «Arrangementstyper» card and no type choice (a new event is an
 *   «Arrangement», community_event; types stay in the database and still drive RSVP defaults, reminders and the janaza
 *   gate). «Hvor mange kommer?», a ticket price and limit, guests, the survey, the campaign on the event, and an amber
 *   warning when another booking has the same room at the same time (components/EventPro.tsx).
 */
import React, { useState } from 'react';
import { call, can, errText, patch, post, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty, useNav } from '../lib/nav';
import { osloTime, wallClock } from '../lib/format';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep, type TagTone } from '../components/ui';
import { TargetPicker } from '../components/TargetPicker';
import { FormDialog } from '../components/dialog';
import { YouTubeLiveCard } from '../components/YouTube';
import { PictureField, sendPicture, type Picked } from '../components/Picture';
import { ProgrammeEditor } from '../components/ProgrammeEditor';
import { BannerPicker, canPickBanner } from '../components/BannerPicker';
import { attachToEvent, bannerTitle, drawForEvent } from '../lib/banner-attach';
import { SeenCount, useSeenCounts } from '../components/SeenCount';
import { ConflictNote, EventFeedback, EventGuests, krFromOre, oreFromKr, ticketText, useConflicts } from '../components/EventPro';
import { kr } from '../lib/format';

/** Owner 2026-10-25: a program gets its own picture, uploaded here; it is the banner in the app, on screens and the website.
 *  Banner studio (2026-10-31): or one of the mosque's banners, drawn here at the screens' size («Velg et banner»). */
const PICTURE_NOTE = 'Vises øverst på arrangementet i appen, på skjermene og på nettsiden. Helst liggende (16:9). Ikke bilder av personer uten at de har sagt ja. JPG, PNG eller WebP — eller velg et av moskeens bannere.';

const PUBLISH_TAG: Record<string, [TagTone, string]> = {
  published: ['green', 'Publisert'], draft: ['grey', 'Utkast'], scheduled: ['gold', 'Planlagt'],
  cancelled: ['red', 'Avlyst'], archived: ['grey', 'Arkivert'],
};
const EV_FIELDS: Record<string, string> = { title_no: 'Tittel', starts_at: 'Dato og tid', ends_at: 'Til', location_no: 'Sted', body_no: 'Beskrivelse', live_url: 'YouTube-lenke', image_urls: 'Bilder',
  type: 'Type', visibility: 'Synlighet', location_url: 'Kartlenke', location_lat: 'Koordinater', location_address: 'Adresse', rsvp: 'Påmelding',
  price_ore: 'Pris', ticket_capacity: 'Antall billetter', rsvp_party_sizes: 'Hvor mange kommer' };
/** The price and limit as the form holds them → the body's fields (an unreadable price is said, not sent). */
function ticketFields(price: string, capacity: string): { ok: true; body: Body } | { ok: false; message: string } {
  const ore = oreFromKr(price);
  if (Number.isNaN(ore)) return { ok: false, message: 'Pris: skriv et beløp i kroner, for eksempel 150.' };
  const cap = capacity.trim() ? Number(capacity.trim()) : null;
  if (cap !== null && !Number.isInteger(cap)) return { ok: false, message: 'Antall billetter: skriv et helt tall.' };
  return { ok: true, body: { price_ore: ore, ticket_capacity: cap } };
}
const VIS_NO: Record<string, string> = { public: 'Alle', members: 'Bare medlemmer' };
const checksText = (checks: Body[] | undefined) => (checks || []).filter((c) => c.severity !== 'low').map((c) => c.message_no).join(' ');

export function Events() {
  // Batch 2A: a «Påmelding» link per event, only when the mosque has the capability (the view itself is in NAV).
  const { caps } = useNav();
  const hasRegistration = Boolean(caps && caps.includes('event_registration'));
  // A3 (2026-10-24): «Direktesending på YouTube (lenken lages automatisk)», only when the mosque has the capability.
  const ytAuto = Boolean(caps && caps.includes('youtube_live_auto'));
  // R3 (2026-10-29): «Program» — the evening's steps, run on the screens from the phone — only with the capability.
  const programmeOn = Boolean(caps && caps.includes('programme_runner')) && (can('programme.run') || can('events.write'));
  // Arrangementer pro (2026-10-31): each part only while the mosque has its capability.
  const on = (k: string) => Boolean(caps && caps.includes(k));
  const ticketsOn = on('event_tickets'), guestsOn = on('contacts_directory'), feedbackOn = on('event_feedback');
  const { data, error, reload } = useLoad<Body>('/events');
  // OA-025: the mosque's own types (its names, its order); only the usable ones can be chosen.
  const types = useLoad<Body>('/event-types');
  const typeList: Body[] = (types.data && types.data.items) || [];
  const typeName = (id: string | null) => (typeList.find((t) => t.id === id) || {}).name_no || null;
  // Owner 2026-10-31: no type choice — a new event is an «Arrangement» (community_event), when the mosque has it on.
  const defaultType = typeList.find((t) => t.key === 'community_event' && t.usable) ? 'community_event' : '';
  const empty = { title: '', date: '', time: '', end: '', endDate: '', place: '', address: '', mapUrl: '', body: '', live: '', banner: '', images: '',
    visibility: '', rsvp: false, partySizes: true, price: '', capacity: '', yt: false, ytPrivacy: 'public' };
  const [f, setF] = useState(empty);
  const [msg, say] = useMsg();
  const [saving, setSaving] = useState(false);
  const [pic, setPic] = useState<Picked>(null);
  // «Velg et banner» (2026-10-31): the picker over the form; the banner is drawn here and sent with the form.
  const [picking, setPicking] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const canPick = canPickBanner(caps);
  async function pickBanner(b: Body) {
    setDrawing(true);
    try {
      const data = await drawForEvent(b);
      if (!data) { say('Banneret kunne ikke tegnes i denne nettleseren. Last det ned fra «Bannere» og last det opp her.', 'err'); return; }
      setPic({ data, bannerId: b.id, bannerTitle: bannerTitle(b) }); setPicking(false);
    } finally { setDrawing(false); }
  }
  // Owner 2026-10-23: the form opens in a popup from «+ Nytt arrangement»; «Sett direkte» asks for its link in its own.
  const [creating, setCreating] = useState(false);
  const [liveFor, setLiveFor] = useState<Body | null>(null);
  const [liveUrl, setLiveUrl] = useState('');
  const typed = Boolean(f.title || f.body || f.place || f.banner || f.images || pic);
  useDirty('event', typed);
  const openNew = () => { say(''); setCreating(true); };
  const closeNew = () => { setCreating(false); setF(empty); setPic(null); };
  const set = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const conflict = useConflicts(creating && f.date && f.time ? `${f.date}T${f.time}:00` : null, f.date && f.end ? `${f.endDate || f.date}T${f.end}:00` : null, f.place);

  async function save(publish: boolean) {
    if (saving) return;
    setSaving(true);
    try { await saveNow(publish); } finally { setSaving(false); }
  }
  async function saveNow(publish: boolean) {
    const tf = ticketsOn ? ticketFields(f.price, f.capacity) : { ok: true as const, body: {} };
    if (!tf.ok) return say(tf.message, 'err');
    const r = await post('/events', {
      title_no: f.title.trim(), body_no: f.body.trim() || null, location_no: f.place.trim() || null,
      image_url: (!pic && f.banner.trim()) || null, image_urls: f.images.split('\n').map(s => s.trim()).filter(Boolean),
      starts_at: f.date && f.time ? `${f.date}T${f.time}:00` : '', ends_at: f.date && f.end ? `${f.endDate || f.date}T${f.end}:00` : null,
      // OA-025/026/028: type, who may see it, a structured place, and RSVP for members.
      ...(defaultType ? { type: defaultType } : {}), ...(f.visibility ? { visibility: f.visibility } : {}),
      location_address: f.address.trim() || null, location_url: f.mapUrl.trim() || null,
      ...(f.rsvp ? { rsvp: { enabled: true, audience: ['members'] }, rsvp_party_sizes: f.partySizes } : {}),
      ...(ticketsOn ? tf.body : {}),
      ...(ytAuto && f.yt ? { youtube_auto: true, youtube_privacy: f.ytPrivacy } : {}),
    });
    if (!r.ok) return say(errText(r.body, EV_FIELDS), 'err');
    let text = r.body.message_no;
    // The picture goes up before publishing, so the notice, the screens and a share have it from the first moment.
    const badPic = await sendPicture(`/events/${r.body.id}/image`, pic);
    if (badPic) text += ` Bildet ble ikke lagret: ${badPic} Legg det inn under «Bilder».`;
    if (publish) {
      const p = await post(`/events/${r.body.id}/publish`);
      if (!p.ok) { say('Lagret som utkast, men ikke publisert: ' + errText(p.body), 'err'); return reload(); }
      text = 'Publisert. ' + (p.body.reaches_no || '') + (checksText(p.body.checks) ? ' Merk: ' + checksText(p.body.checks) : '');
      if (f.yt) text += ' YouTube-lenken lages nå.';
      else if (f.live.trim()) {
        const l = await post(`/events/${r.body.id}/live`, { live_url: f.live.trim() });
        text += l.ok ? ' Satt til DIREKTE.' : ' Direktesending ble ikke startet: ' + errText(l.body);
      }
    }
    say(text, badPic ? 'err' : 'ok');
    setF(empty);
    setPic(null);
    setCreating(false);
    reload();
  }

  async function act(e: Body, what: string) {
    let r;
    if (what === 'publish') r = await post(`/events/${e.id}/publish`);
    if (what === 'cancel') r = await post(`/events/${e.id}/unpublish`, { mode: 'cancel' });
    if (what === 'delete') r = await post(`/events/${e.id}/unpublish`, { mode: 'delete' });
    if (what === 'endlive') r = await post(`/events/${e.id}/live`, { live: false });
    if (what === 'live') {
      if (!liveUrl.trim()) return say('Lim inn YouTube-lenken først.', 'err');
      r = await post(`/events/${e.id}/live`, { live_url: liveUrl.trim() });
      if (r.ok) { setLiveFor(null); setLiveUrl(''); }
    }
    if (!r) return;
    const OK: Record<string, string> = {
      publish: 'Publisert. ' + (r.body.reaches_no || ''), cancel: r.body.message_no || 'Avlyst — det står som AVLYST til det ville vært ferdig.',
      live: 'Satt til DIREKTE.', endlive: 'Direktesendingen er avsluttet.', delete: 'Utkastet er slettet. Det har aldri vært vist noe sted.',
    };
    say(r.ok ? OK[what] : errText(r.body), r.ok ? 'ok' : 'err');
    reload();
  }

  const list: Body[] = (data && (data.items || data.upcoming)) || [];
  // «Åpnet av M» (spec 01 C): signed-in app users who opened the event — only with «Visningstall» on.
  const opened = useSeenCounts('event', list.filter((e) => e.status !== 'draft').map((e) => e.id));
  return (
    <>
      <div className="new-row"><Btn small={false} onClick={openNew}>+ Nytt arrangement</Btn></div>
      {!creating && !liveFor && <Msg text={msg.text} tone={msg.tone} />}
      <FormDialog open={creating} title="Nytt arrangement" dirty={typed} onClose={closeNew}
        hint="Norsk er obligatorisk. Engelsk og urdu oversettes automatisk når du lagrer, og legges i kø for gjennomgang.">
          <Field label="Tittel — norsk" required>{(id) => <input id={id} value={f.title} onChange={set('title')} placeholder="F.eks. Mahfil-e-Milad" autoComplete="off" />}</Field>
          <div className="row">
            <Field label="Dato" required className="field half">{(id) => <input id={id} type="date" value={f.date} onChange={set('date')} />}</Field>
            <Field label="Fra kl." required className="field half">{(id) => <input id={id} type="time" value={f.time} onChange={set('time')} />}</Field>
            <Field label="Til kl." className="field half">{(id) => <input id={id} type="time" value={f.end} onChange={set('end')} />}</Field>
            <Field label="Sted" className="field half">{(id) => <input id={id} value={f.place} onChange={set('place')} placeholder="F.eks. Hovedsalen" />}</Field>
          </div>
          <div className="row">
            <Field label="Til dato" note="For arrangementer over flere dager." className="field half">{(id, d) => <input id={id} aria-describedby={d} type="date" value={f.endDate} onChange={set('endDate')} />}</Field>
            <Field label="Hvem ser det" className="field half">{(id) => (
              <select id={id} value={f.visibility} onChange={set('visibility')}>
                <option value="">Alle</option><option value="members">Bare medlemmer</option>
              </select>)}</Field>
          </div>
          <ConflictNote text={conflict} />
          <div className="row">
            <Field label="Adresse" className="field half">{(id) => <input id={id} value={f.address} onChange={set('address')} placeholder="Gate, postnummer og sted" />}</Field>
            <Field label="Kartlenke" className="field half">{(id) => <input id={id} type="url" value={f.mapUrl} onChange={set('mapUrl')} placeholder="https://…" />}</Field>
          </div>
          <label className="check"><input type="checkbox" checked={f.rsvp} onChange={(e) => setF({ ...f, rsvp: e.target.checked })} /> Medlemmer kan melde seg på («kommer» / «kommer ikke»)</label>
          {f.rsvp && <label className="check"><input type="checkbox" checked={f.partySizes} onChange={(e) => setF({ ...f, partySizes: e.target.checked })} /> Spør «Hvor mange kommer?» (1–10)</label>}
          {ticketsOn && <div className="row">
            <Field label="Billettpris (kr)" note="Tom = gratis. Billetten betales med Vipps i appen." className="field half">{(id, d) => <input id={id} aria-describedby={d} inputMode="decimal" value={f.price} onChange={set('price')} placeholder="F.eks. 150" />}</Field>
            <Field label="Antall billetter" note="Tom = ingen grense." className="field half">{(id, d) => <input id={id} aria-describedby={d} inputMode="numeric" value={f.capacity} onChange={set('capacity')} />}</Field>
          </div>}
          <Field label="Beskrivelse — norsk">{(id) => <textarea id={id} rows={3} value={f.body} onChange={set('body')} placeholder="Hva skjer, og hvem er det for?" />}</Field>
          <PictureField label="Bilde" note={PICTURE_NOTE} picked={pic} onPick={setPic} onChooseBanner={canPick ? () => setPicking(true) : undefined} />
          {!pic && <Field label="… eller lenke til et bilde" note="HTTPS-lenke til et bilde som ligger et annet sted.">{id => <input id={id} type="url" value={f.banner} onChange={set('banner')} placeholder="https://…" />}</Field>}
          <Field label="Flere bilder" note="Én HTTPS-lenke per linje, maks 12. Alle vises inne på arrangementet.">{id => <textarea id={id} rows={3} value={f.images} onChange={set('images')} />}</Field>
          {ytAuto && <>
            <label className="check"><input type="checkbox" checked={f.yt} onChange={(e) => setF({ ...f, yt: e.target.checked })} /> Direktesending på YouTube (lenken lages automatisk)</label>
            {f.yt && <Field label="Synlighet på YouTube" className="field half">{(id) => (
              <select id={id} value={f.ytPrivacy} onChange={set('ytPrivacy')}>
                <option value="public">Offentlig</option><option value="unlisted">Uoppført (bare med lenken)</option>
              </select>)}</Field>}
          </>}
          {!f.yt && <Field label="YouTube-lenke for direktesending" note="Kan også legges inn senere, med «Sett direkte» i listen.">
            {(id, d) => <input id={id} aria-describedby={d} value={f.live} onChange={set('live')} placeholder="https://youtube.com/watch?v=…" />}</Field>}
          <div className="row">
            <Btn ghost disabled={saving} onClick={() => save(false)}>Lagre som utkast</Btn>
            {/* UX audit U-05 (2026-10-19): publishing from the form is two clicks, as «Publiser» in the list below is. */}
            <TwoStep ghost={false} go disabled={saving} label="Publiser nå" busyLabel="Publiserer …"
              confirmLabel="Klikk igjen: vises i appen, på skjermene og nettsiden" onConfirm={() => save(true)} />
          </div>
          <Msg text={msg.text} tone={msg.tone} />
      </FormDialog>
      <BannerPicker open={picking} busy={drawing} onClose={() => setPicking(false)} onPick={(b) => void pickBanner(b)} />
      <FormDialog open={Boolean(liveFor)} title={liveFor ? `Sett «${liveFor.title_no}» direkte` : ''} dirty={Boolean(liveUrl.trim())}
        onClose={() => { setLiveFor(null); setLiveUrl(''); say(''); }}
        hint="Appen, skjermene og nettsiden viser sendingen samtidig.">
        <Field label="YouTube-lenke for direktesending" required>{(id) => <input id={id} value={liveUrl} onChange={(e) => setLiveUrl(e.target.value)} placeholder="https://youtube.com/watch?v=…" />}</Field>
        <div className="row"><Btn onClick={() => { if (liveFor) void act(liveFor, 'live'); }}>Sett direkte</Btn></div>
        <Msg text={msg.text} tone={msg.tone} />
      </FormDialog>
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <Card title={'Publisert' + (data.drafts ? ` · ${data.drafts} utkast` : '')}>
          <table className="t">
            <thead><tr><th scope="col">Arrangement</th><th scope="col">Dato</th><th scope="col">Sted</th><th scope="col">Status</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
            <tbody>
              {list.length ? list.map((e) => {
                const [tone, label] = PUBLISH_TAG[e.status] || ['grey', e.status];
                return (
                  <tr key={e.id}>
                    <td>{e.title_no}{typeName(e.event_type_id) && <span className="note"> · {typeName(e.event_type_id)}</span>}
                      {e.visibility === 'members' && <> <Tag tone="gold">Medlemmer</Tag></>}
                      {e.is_live && <> <Tag tone="red">DIREKTE</Tag></>}
                      <SeenCount counts={opened} id={e.id} label="Åpnet av" block />
                      <ProLine e={e} />
                      <div className="ev-acts">
                      {!['cancelled', 'archived'].includes(e.status) && <EventEditor event={e} ticketsOn={ticketsOn} onSaved={reload} />}
                      {guestsOn && <EventGuests event={e} />}
                      {feedbackOn && e.status === 'published' && e.pro && e.pro.feedback && <EventFeedback event={e} summary={e.pro.feedback} />}
                      <EventImagesEditor event={e} onSaved={reload} />
                      {e.status === 'published' && <EventRsvp event={e} />}
                      {ytAuto && !['cancelled', 'archived'].includes(e.status) && <YouTubeLiveCard event={e} onSaved={reload} />}
                      {!['archived'].includes(e.status) && <EventRelations event={e} all={list} types={typeList} />}
                      {can('push.compose') && ['published', 'scheduled', 'cancelled'].includes(e.status) && <EventPush event={e} />}
                      {!['cancelled', 'archived'].includes(e.status) && <TargetPicker kind="event" id={e.id} />}
                      {programmeOn && !['cancelled', 'archived'].includes(e.status) && <ProgrammeEditor event={e} />}
                      {/* Batch 2A: the staff list for this event, only when the mosque has the capability (the view is absent otherwise). */}
                      {hasRegistration && e.status !== 'draft' && <> <a className="ev-reg-link" href={`#/pamelding?id=${encodeURIComponent(e.id)}`}>Påmelding</a></>}
                      </div></td>
                    <td className="nowrap">{wallClock(e.starts_at)}</td>
                    <td>{e.location_no || '—'}</td>
                    <td><Tag tone={tone}>{label}</Tag></td>
                    <td>{e.status === 'draft' ? <><TwoStep ghost={false} go label="Publiser" busyLabel="Publiserer …" ariaLabel={`Publiser «${e.title_no}»`}
                          confirmLabel="Klikk igjen: vises i appen, på skjermene og nettsiden" onConfirm={() => act(e, 'publish')} />{' '}
                          <TwoStep label="Slett utkast" confirmLabel="Bekreft: slett utkastet" onConfirm={() => act(e, 'delete')} /></>
                      : e.status === 'cancelled' ? <Tag tone="grey">Avlyst</Tag>
                      : e.is_live ? <Btn ghost onClick={() => act(e, 'endlive')}>Avslutt direkte</Btn>
                      : <><Btn ghost onClick={() => { say(''); setLiveUrl(e.live_url || ''); setLiveFor(e); }}>Sett direkte</Btn>{' '}
                          <TwoStep label="Avlys" busyLabel="Avlyser …" ariaLabel={`Avlys «${e.title_no}»`}
                            confirmLabel="Klikk igjen: avlys — kan ikke angres, og de som har lagret eller meldt seg på, varsles" onConfirm={() => act(e, 'cancel')} /></>}</td>
                  </tr>
                );
              }) : <tr><td colSpan={5}>Ingen kommende arrangementer. Lag det første over.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
      {feedbackOn && data && (data.recent || []).length > 0 && (
        <Card title="Nylig avsluttet" hint="Arrangementer den siste uken — her ser du hva deltakerne svarte dagen etter.">
          <ul className="ev-recent">{(data.recent || []).map((e: Body) => (
            <li key={e.id}>{e.title_no} <span className="note">{wallClock(e.starts_at)}</span> <EventFeedback event={{ ...e, status: 'published' }} summary={e.pro && e.pro.feedback} /></li>))}
          </ul>
        </Card>
      )}
    </>
  );
}

/** One line under the title: who is coming (with their parties), the ticket, the campaign's progress, the survey. */
function ProLine({ e }: { e: Body }) {
  const p: Body = e.pro || {};
  const bits: string[] = [];
  if (p.coming && p.coming.answers) bits.push(`${p.coming.people} kommer (${p.coming.answers} svar)`);
  const t = ticketText(e, p.ticketsSold);
  if (t) bits.push(t);
  if (p.campaign) bits.push(`Innsamling «${p.campaign.title}»: ${kr(p.campaign.raisedOre / 100)} av ${kr(p.campaign.goalOre / 100)} (${p.campaign.percent} %)`);
  if (p.feedback && p.feedback.count) bits.push(`★ ${String(Number(p.feedback.average).toFixed(1)).replace('.', ',')} (${p.feedback.count})`);
  if (p.guests) bits.push(`${p.guests} ${p.guests === 1 ? 'gjest' : 'gjester'}`);
  return bits.length ? <div className="note ev-pro">{bits.join(' · ')}</div> : null;
}

/** Editing a draft or a published event (OA-026). Only what changed is sent; the server names the changed fields. */
function EventEditor({ event, ticketsOn, onSaved }: { event: Body; ticketsOn: boolean; onSaved: () => void }) {
  const split = (v: string | null) => (v ? [v.slice(0, 10), v.slice(11, 16)] : ['', '']);
  const [sd, st] = split(event.starts_at); const [ed, et] = split(event.ends_at);
  const [v, setV] = useState({ title: event.title_no || '', date: sd, time: st, endDate: ed, end: et, place: event.location_no || '',
    address: event.location_address || '', mapUrl: event.location_url || '', body: event.body_no || '', visibility: event.visibility || 'public',
    price: krFromOre(event.price_ore), capacity: event.ticket_capacity ? String(event.ticket_capacity) : '', partySizes: event.rsvp_party_sizes !== 0 });
  const [open, setOpen] = useState(false);
  const conflict = useConflicts(open && v.date && v.time ? `${v.date}T${v.time}:00` : null, v.date && v.end ? `${v.endDate || v.date}T${v.end}:00` : null, v.place, event.id);
  const campaign: Body | null = (event.pro && event.pro.campaign) || null;
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const on = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setV({ ...v, [k]: e.target.value });
  return <details onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}><summary>Endre</summary>
    <Field label="Tittel — norsk">{(id) => <input id={id} value={v.title} onChange={on('title')} />}</Field>
    <div className="row">
      <Field label="Dato" className="field half">{(id) => <input id={id} type="date" value={v.date} onChange={on('date')} />}</Field>
      <Field label="Fra kl." className="field half">{(id) => <input id={id} type="time" value={v.time} onChange={on('time')} />}</Field>
      <Field label="Til dato" className="field half">{(id) => <input id={id} type="date" value={v.endDate} onChange={on('endDate')} />}</Field>
      <Field label="Til kl." className="field half">{(id) => <input id={id} type="time" value={v.end} onChange={on('end')} />}</Field>
    </div>
    <div className="row">
      <Field label="Sted" className="field half">{(id) => <input id={id} value={v.place} onChange={on('place')} />}</Field>
      <Field label="Adresse" className="field half">{(id) => <input id={id} value={v.address} onChange={on('address')} />}</Field>
      <Field label="Kartlenke" className="field half">{(id) => <input id={id} type="url" value={v.mapUrl} onChange={on('mapUrl')} />}</Field>
    </div>
    <ConflictNote text={conflict} />
    <div className="row">
      <Field label="Hvem ser det" className="field half">{(id) => (
        <select id={id} value={v.visibility} onChange={on('visibility')}><option value="public">{VIS_NO.public}</option><option value="members">{VIS_NO.members}</option></select>)}</Field>
      {ticketsOn && <>
        <Field label="Billettpris (kr)" note="Tom = gratis." className="field half">{(id, d) => <input id={id} aria-describedby={d} inputMode="decimal" value={v.price} onChange={on('price')} />}</Field>
        <Field label="Antall billetter" note="Tom = ingen grense." className="field half">{(id, d) => <input id={id} aria-describedby={d} inputMode="numeric" value={v.capacity} onChange={on('capacity')} />}</Field>
      </>}
    </div>
    <label className="check"><input type="checkbox" checked={v.partySizes} onChange={(e) => setV({ ...v, partySizes: e.target.checked })} /> Spør «Hvor mange kommer?» når noen melder seg på</label>
    {campaign && <p className="note">Innsamling til arrangementet: «{campaign.title}» — {kr(campaign.raisedOre / 100)} av {kr(campaign.goalOre / 100)} ({campaign.percent} %). Endres under Innsamlinger.</p>}
    <Field label="Beskrivelse — norsk">{(id) => <textarea id={id} rows={3} value={v.body} onChange={on('body')} />}</Field>
    <Btn disabled={busy} onClick={async () => {
      setBusy(true);
      try {
        const tf = ticketsOn ? ticketFields(v.price, v.capacity) : { ok: true as const, body: {} };
        if (!tf.ok) { setMessage(tf.message); return; }
        const r = await patch(`/events/${event.id}`, {
          title_no: v.title.trim(), body_no: v.body.trim() || null, location_no: v.place.trim() || null,
          location_address: v.address.trim() || null, location_url: v.mapUrl.trim() || null,
          starts_at: v.date && v.time ? `${v.date}T${v.time}:00` : event.starts_at,
          ends_at: v.end ? `${v.endDate || v.date}T${v.end}:00` : null,
          visibility: v.visibility, rsvp_party_sizes: v.partySizes, ...(ticketsOn ? tf.body : {}),
        });
        if (!r.ok) { setMessage(errText(r.body, EV_FIELDS)); return; }
        const moved = r.body.reminders && r.body.reminders.moved ? ` ${r.body.reminders.moved} påminnelse(r) flyttet med.` : '';
        setMessage(r.body.changed.length ? `Lagret. Appen, skjermene og nettsiden viser endringen ved neste oppdatering.${moved}` : 'Ingen endring.');
        if (r.body.changed.length) onSaved();
      } finally { setBusy(false); }
    }}>Lagre endringer</Btn><p role="status">{message}</p>
  </details>;
}

/** RSVP for staff (OA-028): counts for everyone who runs events; who answered only with its own permission (logged). */
function EventRsvp({ event }: { event: Body }) {
  const [data, setData] = useState<Body | null>(null);
  const [who, setWho] = useState<Body[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const load = async () => { setFailed(null); try { setData(await call(`/events/${event.id}/rsvp`)); } catch (x) { setFailed((x as Error).message); } };
  return <details onToggle={(e) => { if ((e.target as HTMLDetailsElement).open && !data) void load(); }}>
    <summary>Påmelding</summary>
    {failed ? <FoldError error={failed} retry={load} /> : !data ? <p className="note" role="status">Henter …</p> : !data.policy || !data.policy.enabled ? <p className="note">Ikke åpen for påmelding.</p> : (
      <>
        <p>{data.counts.COMING} kommer{data.counts.people !== undefined && data.counts.people !== data.counts.COMING ? ` (${data.counts.people} personer i alt)` : ''} · {data.counts.NOT_COMING} kommer ikke</p>
        {data.canSeeAttendees && !who && <Btn ghost onClick={async () => {
          try { const r = await call(`/events/${event.id}/attendees`); setWho(r.items || []); } catch (x) { setFailed((x as Error).message); }
        }}>Vis hvem (logges)</Btn>}
        {who && <ul>{who.map((a, i) => <li key={i}>{a.name || 'Uten navn'}{a.memberNo ? ` (nr. ${a.memberNo})` : ''} — {a.response === 'COMING' ? `kommer${a.partySize > 1 ? `, ${a.partySize} personer` : ''}` : 'kommer ikke'}{a.note ? ` · «${a.note}»` : ''}</li>)}</ul>}
      </>
    )}
  </details>;
}

/**
 * Related events (OA-027, technical part): a janaza → its burial → its fatiha, or any two events. Linking publishes
 * nothing and sends nothing; the app shows a linked event only once it is itself published. The relation names come
 * from the mosque's own type names where there is one (a renamed «Fatiha» stays renamed).
 */
function EventRelations({ event, all, types }: { event: Body; all: Body[]; types: Body[] }) {
  const [data, setData] = useState<Body | null>(null);
  const [child, setChild] = useState('');
  const [kind, setKind] = useState('RELATED');
  const [msg, say] = useMsg();
  const typeName = (key: string) => (types.find((t) => t.key === key) || {}).name_no;
  const REL_NO: Record<string, string> = { BURIAL: typeName('burial') || 'Begravelse', FATIHA: typeName('fatiha') || 'Minnesamling', FOLLOW_UP: 'Oppfølging', RELATED: 'Relatert' };
  const [failed, setFailed] = useState<string | null>(null);
  const load = async () => { setFailed(null); try { setData(await call(`/events/${event.id}/relations`)); } catch (x) { setFailed((x as Error).message); } };
  const add = async () => {
    const r = await post(`/events/${event.id}/relations`, { childId: child, type: kind });
    say(r.ok ? 'Koblet. Ingenting er publisert eller varslet av dette.' : errText(r.body), r.ok ? 'ok' : 'err');
    if (r.ok) { setData({ ...data, items: r.body.items }); setChild(''); }
  };
  const remove = async (otherId: string) => {
    const r = await send(`/events/${event.id}/relations/${encodeURIComponent(otherId)}`, { method: 'DELETE' });
    if (r.ok) setData({ ...data, items: r.body.items }); else say(errText(r.body), 'err');
  };
  const others = all.filter((x) => x.id !== event.id && !(data?.items || []).some((i: Body) => i.event && i.event.id === x.id));
  return <details onToggle={(e) => { if ((e.target as HTMLDetailsElement).open && !data) void load(); }}>
    <summary>Koblede arrangementer{data && data.items.length ? ` (${data.items.length})` : ''}</summary>
    {failed ? <FoldError error={failed} retry={load} /> : !data ? <p className="note" role="status">Henter …</p> : <>
      {data.items.length ? <ul>{data.items.map((i: Body) => (
        <li key={i.event?.id}>{i.direction === 'parent' ? 'Hører til: ' : `${REL_NO[i.relation] || i.relation}: `}{i.event ? i.event.title_no : '—'}
          {i.event && i.event.status !== 'published' && <span className="note"> ({PUBLISH_TAG[i.event.status]?.[1] || i.event.status})</span>}
          {i.direction === 'child' && <> <Btn ghost onClick={() => remove(i.event.id)}>Fjern</Btn></>}</li>))}</ul>
        : <p className="note">Ingen koblinger.</p>}
      <div className="row">
        <select aria-label="Arrangement som skal kobles" value={child} onChange={(e) => setChild(e.target.value)}>
          <option value="">Velg arrangement …</option>
          {others.map((x) => <option key={x.id} value={x.id}>{x.title_no} — {wallClock(x.starts_at)}</option>)}
        </select>
        <select aria-label="Hva slags kobling" value={kind} onChange={(e) => setKind(e.target.value)}>
          {(data.types || []).map((k: string) => <option key={k} value={k}>{REL_NO[k] || k}</option>)}
        </select>
        <Btn ghost onClick={add}>Koble</Btn>
      </div>
      <p className="note">En kobling publiserer ikke og sender ingen varsler. Hvert arrangement publiseres for seg.</p>
      <Msg text={msg.text} tone={msg.tone} />
    </>}
  </details>;
}

/**
 * Telling people about an event (OA-031). A push to the congregation is a DRAFT made from the event, sent for a
 * second person's approval in Varsler — nothing goes out from here. Below it: the change and cancellation notices the
 * server sent to the people who kept the event (counts only), when `event_updates` is on.
 */
const NOTICE_NO: Record<string, string> = { sent: 'sendt', skipped: 'ikke sendt', failed: 'kom ikke fram' };
const REASON_NO: Record<string, string> = { unchanged: 'endret tilbake', capability_off: 'funksjonen er av', time_passed: 'arrangementet var over',
  nobody_kept_it: 'ingen har lagret det', no_device: 'ingen telefon registrert', no_transport: 'varsler er av på serveren', not_delivered: 'leverte ikke' };
function EventPush({ event }: { event: Body }) {
  const [data, setData] = useState<Body | null>(null);
  const [draft, setDraft] = useState<Body | null>(null);
  const [msg, say] = useMsg();
  const [failed, setFailed] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const load = async () => { setFailed(null); try { const r = await call(`/events/${event.id}/push`); setData(r); setDraft(r.draft); } catch (x) { setFailed((x as Error).message); } };
  const submit = async () => {
    if (sending) return;
    setSending(true);
    const r = await post('/push', draft).finally(() => setSending(false));
    say(r.ok ? 'Sendt til godkjenning. Ingenting går ut før en annen har godkjent det under Varsler.' : errText(r.body), r.ok ? 'ok' : 'err');
    if (r.ok) void load();
  };
  const open = (data?.pushes || []).find((p: Body) => p.status !== 'failed');
  return <details onToggle={(e) => { if ((e.target as HTMLDetailsElement).open && !data) void load(); }}>
    <summary>Varsel{data?.suggested && !open ? ' (foreslått for denne typen)' : ''}</summary>
    {failed ? <FoldError error={failed} retry={load} /> : !data || !draft ? <p className="note" role="status">Henter …</p> : <>
      {open ? <p>Varsel om arrangementet: <Tag tone={open.status === 'sent' ? 'green' : 'gold'}>{open.status === 'sent' ? `sendt${open.people != null ? ` til ${open.people}` : ''}` : 'venter på godkjenning'}</Tag></p>
        : !data.canSend ? <p className="note">Publiser arrangementet først.</p> : <>
          <Field label="Tittel">{(id, d) => <input id={id} aria-describedby={d} maxLength={65} value={draft.title_no} onChange={(e) => setDraft({ ...draft, title_no: e.target.value })} />}</Field>
          <Field label="Tekst">{(id, d) => <input id={id} aria-describedby={d} maxLength={178} value={draft.body_no} onChange={(e) => setDraft({ ...draft, body_no: e.target.value })} />}</Field>
          <p className="note">Mottakere: {data.audience_no}</p>
          <Btn ghost disabled={sending} aria-busy={sending || undefined} onClick={submit}>{sending ? 'Sender …' : 'Send til godkjenning'}</Btn>
        </>}
      <p className="note">{data.updates.on
        ? `Endringer i tid og sted, og avlysning, varsles automatisk til de ${data.updates.keptBy} som har lagret, meldt seg på eller satt påminnelse.`
        : 'Automatisk varsel om endringer er ikke slått på for moskeen.'}</p>
      {data.notices.length > 0 && <ul>{data.notices.map((x: Body, i: number) => (
        <li key={i}>{x.kind === 'cancelled' ? 'Avlysning' : 'Endring'}: {NOTICE_NO[x.status] || x.status}
          {x.status === 'sent' ? ` til ${x.delivered} av ${x.recipients}` : x.reason ? ` (${REASON_NO[x.reason] || x.reason})` : ''} · {osloTime(x.at)}</li>))}</ul>}
      <Msg text={msg.text} tone={msg.tone} />
    </>}
  </details>;
}

/** A folded section that could not be fetched: said, with a way to try again (it showed «Henter …» for ever). */
function FoldError({ error, retry }: { error: string; retry: () => void }) {
  return <div><p className="msg err" role="alert" style={{ marginTop: 0 }}>Kunne ikke hente dette: {error}</p><Btn ghost onClick={retry}>Prøv igjen</Btn></div>;
}

function EventImagesEditor({ event, onSaved }: { event: Body; onSaved: () => void }) {
  const { caps } = useNav();
  const [banner, setBanner] = useState(event.image_url || '');
  const [images, setImages] = useState(() => { try { return JSON.parse(event.image_urls || '[]').join('\n'); } catch { return ''; } });
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [pic, setPic] = useState<Picked>(null);
  // «Velg et banner» (2026-10-31): a banner drawn here becomes the picture; the server remembers which (`event.banner`),
  // and says when that banner has changed since — then «Oppdater fra banneret» draws it again.
  const [picking, setPicking] = useState(false);
  const canPick = canPickBanner(caps);
  const from: Body | null = event.banner || null;
  // An uploaded picture's address is the server's own (…/events/:id/image?v=…): it is replaced or removed here, not typed.
  const uploaded = typeof event.image_url === 'string' && /\/events\/[^/]+\/image\?v=/.test(event.image_url);
  async function pickBanner(b: Body) {
    setBusy(true);
    try {
      const data = await drawForEvent(b);
      if (!data) { setMessage('Banneret kunne ikke tegnes i denne nettleseren.'); return; }
      setPic({ data, bannerId: b.id, bannerTitle: bannerTitle(b) }); setPicking(false);
    } finally { setBusy(false); }
  }
  async function redraw() {
    if (!from) return;
    setBusy(true);
    try {
      const list = await call('/banners');
      const b = (list.items || []).find((x: Body) => x.id === from.id);
      if (!b) { setMessage('Fant ikke banneret lenger.'); return; }
      const bad = await attachToEvent(event.id, b);
      setMessage(bad || 'Bildet er laget på nytt fra banneret.');
      if (!bad) onSaved();
    } catch (x) { setMessage((x as Error).message); } finally { setBusy(false); }
  }
  return <details><summary>Bilder</summary>
    <PictureField label="Bilde" note={PICTURE_NOTE} current={uploaded ? event.image_url : null} currentFrom={uploaded && from ? from.title : null}
      picked={pic} onPick={setPic} onChooseBanner={canPick ? () => setPicking(true) : undefined} />
    {uploaded && from && from.changed && !pic && <p className="msg warn" role="status">Banneret «{from.title}» er endret etter at bildet ble laget.{' '}
      <Btn ghost disabled={busy} onClick={() => void redraw()}>Oppdater fra banneret</Btn></p>}
    {canPick && <p className="note"><a href={`#/bannere/ny?arrangement=${encodeURIComponent(event.id)}`}>Lag banner fra arrangementet</a> — tittel, dato og sted fylles inn, og banneret legges her som bilde når det lagres.</p>}
    <BannerPicker open={picking} busy={busy} onClose={() => setPicking(false)} onPick={(b) => void pickBanner(b)} />
    {pic && <Btn disabled={busy} onClick={async () => {
      setBusy(true);
      try {
        const bad = await sendPicture(`/events/${event.id}/image`, pic);
        setMessage(bad || 'Bildet er lagret.');
        if (!bad) { setPic(null); onSaved(); }
      } finally { setBusy(false); }
    }}>Lagre bildet</Btn>}
    {!uploaded && <Field label="… eller lenke til et bilde">{id => <input id={id} type="url" value={banner} onChange={e => setBanner(e.target.value)} />}</Field>}
    <Field label="Flere bilder" note="Én HTTPS-lenke per linje, maks 12.">{id => <textarea id={id} rows={3} value={images} onChange={e => setImages(e.target.value)} />}</Field>
    <Btn disabled={busy} onClick={async () => {
      setBusy(true);
      try {
        const result = await post(`/events/${event.id}/images`, { ...(uploaded ? {} : { image_url: banner.trim() || null }), image_urls: images.split('\n').map((s: string) => s.trim()).filter(Boolean) });
        setMessage(result.ok ? 'Bildene er lagret.' : errText(result.body, EV_FIELDS));
        if (result.ok) onSaved();
      } finally { setBusy(false); }
    }}>Lagre bilder</Btn><p role="status">{message}</p>
  </details>;
}
